import { FC, useMemo } from 'react';
import type {
  InitialMapState,
  MapSearchResultEvent,
  MapSearchStatusEvent,
  MapSelection,
  MapStateChangeEvent,
  MapWidgetState,
  SearchFilters,
} from '@propileya/geo-widget';
import { IHousingObjectResponse, OfferType, SearchTabs } from '@/types';
import { ContentWrapper } from '../../styles';
import { RowsContent } from './RowsContent';
import { GridContent } from './GridContent';
import { MapContent } from './MapContent';
import { CustomPagination } from '@/components';

interface ContentProps {
  isLoading: boolean;
  objects?: IHousingObjectResponse[];
  view: SearchTabs | null;
  page: number;
  numFound?: number;
  itemsPerPage?: number;
  onPageChange: (value: number) => void;
  onItemsPerPageChange: (value: number) => void;
  offerType: OfferType;
  priceValue?: number[];
  rentPriceValue?: number[];
  sorting?: string;
  isProject?: boolean;
  // Map-specific props — only used when view === MAP
  mapFilters?: SearchFilters;
  mapSelection?: MapSelection;
  mapInitialState?: InitialMapState;
  onMapReady?: (state: MapWidgetState) => void;
  onMapStateChange?: (event: MapStateChangeEvent) => void;
  onMapSearchResult?: (event: MapSearchResultEvent) => void;
  onMapSearchStatusChange?: (event: MapSearchStatusEvent) => void;
}

export const Content: FC<ContentProps> = ({
  objects,
  isLoading,
  view,
  page,
  numFound,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  offerType,
  isProject,
  mapFilters,
  mapSelection,
  mapInitialState,
  onMapReady,
  onMapStateChange,
  onMapSearchResult,
  onMapSearchStatusChange,
}) => {
  const isMapView = view === SearchTabs.MAP;

  const content = useMemo(
    () => ({
      [SearchTabs.TILE]: (
        <GridContent
          objects={objects}
          offerType={offerType}
          isLoading={isLoading}
          isProject={isProject}
        />
      ),
      [SearchTabs.LIST]: (
        <RowsContent
          objects={objects}
          offerType={offerType}
          isLoading={isLoading}
          isProject={isProject}
        />
      ),
      [SearchTabs.MAP]:
        mapFilters && mapSelection && mapInitialState ? (
          <MapContent
            filters={mapFilters}
            selection={mapSelection}
            initialState={mapInitialState}
            onReady={onMapReady}
            onStateChange={onMapStateChange}
            onSearchResult={onMapSearchResult}
            onSearchStatusChange={onMapSearchStatusChange}
          />
        ) : null,
    }),
    [
      objects,
      isLoading,
      offerType,
      isProject,
      mapFilters,
      mapSelection,
      mapInitialState,
      onMapReady,
      onMapStateChange,
      onMapSearchResult,
      onMapSearchStatusChange,
    ],
  );

  return (
    <ContentWrapper $isMap={isMapView}>
      {view !== null ? content[view] : null}
      {!isMapView && (
        <CustomPagination
          page={page}
          numFound={numFound}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      )}
    </ContentWrapper>
  );
};
